import { useState, useCallback } from 'react';
import { fetchZoomToken } from '../services/api';
import { sendMessageToParent } from '../utils/apiUtils';
import { saveZoomSession, getZoomSession, clearZoomSession } from '../utils/sessionStorage';

/**
 * Custom hook for managing Zoom session state and operations
 * Simplified version using unified storage utilities
 */
export const useZoomSession = ({ miraSolCaseId }) => {
  // Session state
  const [session, setSession] = useState(null);
  const [zoomError, setZoomError] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('unknown');

  // Create new meeting
  const createMeeting = useCallback(async (formData) => {
    console.log('[useZoomSession] Creating new meeting with details:', formData);
    setZoomError(null);

    if (!formData.topic || !formData.attendeeName) {
      setZoomError({
        title: "Missing Information",
        message: "Please provide a topic and your name to create a meeting.",
        suggestion: "Fill in all required fields."
      });
      return;
    }

    try {
      // Request host token from the backend
      const data = await fetchZoomToken({
        session_id: formData.sessionId || 'new',
        role: 'host',
        topic: formData.topic
      });
      console.log('[useZoomSession] Host token received for new meeting:', data);

      // Set session state
      const newSession = {
        sessionId: data.session_id,
        topic: data.topic || formData.topic,
        token: data.token,
        passcode: data.passcode || ''
      };
      setSession(newSession);

      // Set host status
      setIsHost(true);

      // Store session data for persistence
      const sessionData = {
        sessionId: data.session_id,
        passcode: data.passcode || '',
        topic: data.topic || formData.topic,
        attendeeName: formData.attendeeName,
        role: 'host'
      };
      saveZoomSession(sessionData);

      return newSession;
    } catch (error) {
      console.error('[useZoomSession] Error creating meeting:', error);
      setZoomError({
        title: "Failed to Create Meeting",
        message: error.message || "Could not create the meeting. Please try again.",
        suggestion: "Check your internet connection and try again. If the problem persists, contact support."
      });
      throw error;
    }
  }, []);

  // Join existing meeting
  const joinMeeting = useCallback(async (userName, passcode, topic, role) => {
    console.log('[useZoomSession] Joining meeting with params:', { userName, passcode, topic, role });
    setZoomError(null);

    if (!userName || !topic || !passcode) {
      console.error('[useZoomSession] joinMeeting called with missing required information.');
      setZoomError({
        title: "Missing Information",
        message: "Required information (User Name, Topic, Passcode) is missing to join the meeting.",
        suggestion: "Ensure all necessary details are provided."
      });
      return;
    }

    try {
      const payload = { role: role, topic: topic, passcode: passcode };
      console.log('[useZoomSession] FINAL PAYLOAD BEING SENT:', payload);
      const data = await fetchZoomToken(payload);
      console.log('[useZoomSession] Joined meeting successfully:', data);
      // Set session state
      const newSession = {
        sessionId: data.session_id,
        topic: data.topic || topic,
        token: data.token,
        passcode: data.passcode || passcode || ''
      };
      setSession(newSession);

      // Generate and set the invite link
      const baseUrl = window.location.origin;
      const encodedSessionId = encodeURIComponent(data.sessionData?.topic || data.topic || topic);
      const encodedPasscode = encodeURIComponent(data.passcode || passcode || '');
      const generatedInviteLink = `${baseUrl}/?session_topic=${encodedSessionId}&passcode=${encodedPasscode}`;
      setInviteLink(generatedInviteLink);

      // Communicate invite link to the parent window
      sendMessageToParent('inviteLinkAvailable', { inviteLink: generatedInviteLink });
      console.log(`[useZoomSession] Invite link available: ${generatedInviteLink}. Message sent to parent.`);

      // Set host status based on role
      setIsHost(role === 'host');

      // Store session data for persistence
      const sessionData = {
        sessionId: data.session_id,
        passcode: data.passcode || passcode || '',
        topic: data.topic || topic,
        attendeeName: userName,
        role: role
      };
      saveZoomSession(sessionData);

      return newSession;
    } catch (error) {
      console.error('[useZoomSession] Error joining meeting:', error);
      
      // Clear stored session if it's a 404 error (session not found)
      if (error.message && error.message.includes('404')) {
        console.log('[useZoomSession] Session not found (404), clearing stored session');
        clearZoomSession();
      }
      
      setZoomError({
        title: "Failed to Join Meeting",
        message: error.message || "Could not join the meeting. Please check the session ID and try again.",
        suggestion: "Verify the session ID is correct and that the meeting is still active. Contact the meeting host if needed."
      });
      throw error;
    }
  }, []);

  // Handle session closed
  const handleSessionClosed = useCallback(() => {
    console.log('[useZoomSession] Session closed callback triggered');

    // Clear session state
    setSession(null);
    setIsHost(false);
    setIsRecording(false);
    setInviteLink(null);

    // Clear session storage
    clearZoomSession();

    console.log('[useZoomSession] Session cleanup completed');

    // Communicate session ended to the parent window
    sendMessageToParent('sessionEnded', { miraSolCaseId: miraSolCaseId });
    console.log(`[useZoomSession] Session ended. Message sent to parent for case ID: ${miraSolCaseId}.`);
  }, [miraSolCaseId]);

  // Load session from storage
  const loadStoredSession = useCallback(() => {
    const storedSessionData = getZoomSession();
    if (storedSessionData) {
      const { sessionId, passcode, topic, attendeeName, role } = storedSessionData;
      if (sessionId && topic && attendeeName && role) {
        console.log('[useZoomSession] Found active session in storage:', { sessionId, topic, attendeeName, role });
        return { sessionId, passcode, topic, attendeeName, role };
      } else {
        console.warn('[useZoomSession] Invalid/incomplete session data. Clearing.');
        clearZoomSession();
      }
    }
    return null;
  }, []);

  // Clear session
  const clearSession = useCallback(() => {
    setSession(null);
    setZoomError(null);
    setIsHost(false);
    setIsRecording(false);
    setInviteLink(null);
    setNetworkStatus('unknown');
    clearZoomSession();
  }, []);

  // Helper function to return topic and passcode as-is (no swapping needed)
  const getCorrectMapping = useCallback((topicValue, passcodeValue) => {
    return { topic: topicValue, passcode: passcodeValue };
  }, []);

  return {
    // State
    session, zoomError, isHost, isRecording, inviteLink, networkStatus,
    
    // Actions
    createMeeting, joinMeeting, handleSessionClosed, loadStoredSession,
    clearSession, setZoomError, setIsRecording, setNetworkStatus, getCorrectMapping
  };
};

export default useZoomSession;